import { format, isValid, parse, parseISO } from "date-fns";

export const validateName = (name: string): boolean => {
  const nameRegex = /^[a-zA-ZÀ-ÿ'´`-]+(?: [a-zA-ZÀ-ÿ'´`-]+)*$/;
  return nameRegex.test(name);
};

export const validateEmail = (email: string): boolean => {
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailRegex.test(email);
};

export const maskCPF = (value: string): string | undefined => {
  return value
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/(\d{3})(\d{2})$/, "$1-$2");
};

export const maskCNPJ = (value: string): string | undefined => {
  return value
    .replace(/\D/g, "")
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

export const formatCurrency = (v: string) => {
  if (v === null || v === undefined) return "";
  const value = String(v);
  const removeChar = value.replace(/\D/g, "");
  const number = Number(removeChar) / 100;
  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

export const formatCurrencyInput = (value: string | number) => {
  if (!value) return "";
  
  // Mesma lógica da sua função original para limpar
  const onlyDigits = String(value).replace(/\D/g, "");

  if (onlyDigits === "") return "";

  const number = Number(onlyDigits) / 100;

  // Diferença: NÃO usa style: 'currency' para não colocar o R$
  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Converte de volta para número para enviar à API/Banco
// Ex: Recebe "1.500,00" e retorna 1500.00
export const parseCurrencyToNumber = (value: string | number) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  
  // Remove pontos de milhar e troca vírgula por ponto
  return parseFloat(value.replace(/\./g, "").replace(",", "."));
};

export const maskPhone = (phone: string) => {
  return phone
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/^(\d{2})(\d)/, "($1) $2") // adiciona DDD
    .replace(/(\d{5})(\d{1,4})$/, "$1-$2"); // adiciona hífen
};

export const maskCEP = (cep: string) => {
  if (!cep) return;
  return cep
    .replace(/\D/g, "")
    .slice(0, 8)
    .replace(/(\d{5})(\d{3})/, "$1-$2");
};

export const formatDate = (
  date: Date | string | number | undefined | null,
  brazilianFormat = false,
  withTime = false,
): string | undefined => {
  if (!date) return undefined;

  try {
    let dateObj: Date;

    // 1. Conversão para Objeto Date
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === "string") {
      const trimmedDate = date.trim();

      // Regex para detectar formato brasileiro: DD/MM/YYYY (com ou sem hora)
      // Verifica se o terceiro bloco de números tem 4 dígitos
      const isBrazilian = /^\d{2}\/\d{2}\/\d{4}/.test(trimmedDate);

      if (isBrazilian) {
        // Se tem hora (ex: 19/12/2025 18:49:55)
        if (trimmedDate.length > 10) {
          dateObj = parse(trimmedDate, "dd/MM/yyyy HH:mm:ss", new Date());
        } else {
          // Se é apenas data (ex: 19/12/2025)
          dateObj = parse(trimmedDate, "dd/MM/yyyy", new Date());
        }
      } else {
        // Tenta parseISO (padrão internacional YYYY-MM-DD)
        dateObj = parseISO(trimmedDate);

        // Fallback para o construtor padrão se o parseISO falhar
        if (!isValid(dateObj)) {
          dateObj = new Date(trimmedDate);
        }
      }
    } else {
      dateObj = new Date(date);
    }

    // 2. Validação final
    if (!isValid(dateObj)) {
      console.warn("Data inválida fornecida:", date);
      return undefined;
    }

    // 3. Definição do Formato de Saída
    const formatStr = brazilianFormat
      ? withTime
        ? "dd/MM/yyyy HH:mm:ss"
        : "dd/MM/yyyy"
      : withTime
        ? "yyyy-MM-dd HH:mm:ss"
        : "yyyy-MM-dd";

    return format(dateObj, formatStr);
  } catch (err) {
    console.error("Erro crítico ao formatar data:", date, err);
    return undefined;
  }
};

export const validarCNPJ = (cnpj: string) => {
  cnpj = cnpj.replace(/\D/g, "");

  if (cnpj.length !== 14) return false;

  if (/^(\d)\1{13}$/.test(cnpj)) return false;

  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  const digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;

  for (let i = tamanho; i >= 1; i--) {
    soma += Number(numeros.charAt(tamanho - i)) * pos--;

    if (pos < 2) pos = 9;
  }

  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== Number(digitos.charAt(0))) return false;

  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;

  for (let i = tamanho; i >= 1; i--) {
    soma += Number(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== Number(digitos.charAt(1))) return false;

  return true;
};

export const validarCPF = (cpf: string) => {
  cpf = cpf.replace(/\D/g, "");

  if (cpf.length !== 11) return false;

  if (/^(\d)\1{10}$/.test(cpf)) return false;

  let soma = 0;
  let resto: number;

  for (let i = 1; i <= 9; i++) {
    soma += Number(cpf.charAt(i - 1)) * (11 - i);
  }

  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;

  if (resto !== Number(cpf.charAt(9))) return false;

  soma = 0;

  for (let i = 1; i <= 10; i++) {
    soma += Number(cpf.charAt(i - 1)) * (12 - i);
  }

  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;

  if (resto !== Number(cpf.charAt(10))) return false;

  return true;
};

export const validarRG = (rg: string) => {
  if (!rg) return "";
  rg = rg.replace(/[^\dXx]/g, "");

  if (rg.length < 7 || rg.length > 9) return rg;

  return rg;
};

export const formatListToRender = (list: any[]) => {
  if (list.length <= 1) return list.join("");

  return `${list.slice(0, -1).join(", ")} e ${list.at(-1)}`;
}